import { isForecastTrumpingSource } from "constants/settings.constants";
import { ICheckedForecast } from "store/features/app/appSlice";

import { numberToRescatString } from "components/sync/util";

import { ForecastTrumpParamsType } from "../../models/schemas/data.schema";

export function buildForecastTrumpingSettings(
  checkedGlobalForecasts: ICheckedForecast[],
  specialForecastFolders: string[],
  syncJob?: string,
  syncJobRescatCode?: string,
  panelReserveCategory?: string
): ForecastTrumpParamsType {
  const userForecastFolderId = checkedGlobalForecasts[0]?.folderId ?? "";
  // The panel's reserve category is what the property's `.fcst.<rescat>` suffix is built from and
  // what the chart service's own derivation reads, so it wins — but only when it is a category
  // the folder being sent actually carries (two reserve-category groups of one folder can both be
  // checked), or when that folder's rows carry none (forecasts ticked one by one, which is when
  // the fallback matters: without any rescat the data service's user-folder lookup does not match
  // on the folder's category and a folder on a non-default category resolves nothing). The panel
  // value is written from the LAST checked node, so in Compare mode with the group-by on trumping
  // it can belong to another folder; sending this folder's id with that category resolves
  // nothing, so the folder's own first category is used instead. Never another folder's.
  const ownRescats = checkedGlobalForecasts
    .filter((f) => f?.folderId === userForecastFolderId && f?.reserveCategory)
    .map((f) => f.reserveCategory as string);
  const panelFitsFolder =
    !!panelReserveCategory &&
    (ownRescats.length === 0 || ownRescats.includes(panelReserveCategory));
  // A category is only meaningful next to a folder: after a project switch the checked list is
  // cleared but the panel's category is not, and a bare stale category must not go out.
  const userForecastRescat = userForecastFolderId
    ? panelFitsFolder
      ? panelReserveCategory
      : ownRescats[0] ?? panelReserveCategory
    : undefined;
  const forecastSources = [
    ...specialForecastFolders,
    ...(userForecastFolderId ? ["User"] : []),
    ...(syncJob ? ["Synced Job"] : [])
  ];
  return {
    forecastSources,
    userForecastFolderId,
    ...(syncJob ? { syncJob } : {}),
    // The sync state holds the ValNav code; the data service matches synced performance on the name.
    ...(syncJobRescatCode
      ? { syncJobRescat: numberToRescatString(syncJobRescatCode) }
      : {}),
    ...(userForecastRescat ? { userForecastRescat } : {})
  };
}

/**
 * True when any of the given GROUP-BY forecast folders is on Forecast Trumping.
 *
 * Scope matters here. On the GroupBy request `forecastTrumpingSettings` is request-global once it
 * reaches the data service: it enters trump mode on the settings being present at all, without
 * checking the column's own folder. So pass only group-by folders here. Passing an axis folder
 * would put a group-by on an explicit source into trump mode as a side effect.
 *
 * Chart AXIS trumping (EVA-199) uses the same settings object, sent at the top level of the chart
 * request instead. The chart service forwards it to the data service for the trumping axes only,
 * so every surface — focus, legend, map, data table and chart axes — trumps from one definition.
 *
 * Accepts either spelling of the value; the two writers disagree (see isForecastTrumpingSource).
 */
export function hasForecastTrumpingField(forecastFolders: Maybe<string>[]): boolean {
  return forecastFolders.some(isForecastTrumpingSource);
}
